import React from 'react';
import PropTypes from 'prop-types';
import { Box, FormControl, FormHelperText, FormLabel, IconButton, Paper } from '@material-ui/core';
import FormatBoldIcon from '@material-ui/icons/FormatBold';
import FormatItalicIcon from '@material-ui/icons/FormatItalic';
import FormatUnderlinedIcon from '@material-ui/icons/FormatUnderlined';
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';

const htmlPattern = /<\/?[a-z][\s\S]*>/i;

const decodeHtmlEntities = (value = '') =>
  value
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#39;/gi, "'")
    .replace(/&quot;/gi, '"');

const escapeHtml = (value = '') =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const toEditorHtml = (value = '') => {
  if (!value) {
    return '';
  }
  if (htmlPattern.test(value) || value.includes('&nbsp;')) {
    return value;
  }
  return escapeHtml(value).replace(/\r?\n/g, '<br />');
};

const stripHtml = (value = '') => decodeHtmlEntities(value.replace(/<[^>]*>/g, ' ')).replace(/\s+/g, ' ').trim();

const toolbarButtons = [
  { icon: FormatBoldIcon, command: 'bold', label: 'Bold' },
  { icon: FormatItalicIcon, command: 'italic', label: 'Italic' },
  { icon: FormatUnderlinedIcon, command: 'underline', label: 'Underline' },
  { icon: FormatListBulletedIcon, command: 'insertUnorderedList', label: 'Bullet list' },
];

const RichTextEditor = React.forwardRef(function RichTextEditor(
  { label, value, onChange, placeholder, helperText, error, disabled },
  ref
) {
  const editorRef = React.useRef(null);
  const [isFocused, setIsFocused] = React.useState(false);
  const normalizedValue = React.useMemo(() => toEditorHtml(value), [value]);

  React.useImperativeHandle(ref, () => ({
    focus: () => {
      if (editorRef.current) {
        editorRef.current.focus();
      }
    },
  }));

  React.useEffect(() => {
    if (!editorRef.current) {
      return;
    }
    const currentHtml = editorRef.current.innerHTML;
    if (currentHtml !== normalizedValue) {
      editorRef.current.innerHTML = normalizedValue || '';
    }
  }, [normalizedValue]);

  const emitChange = React.useCallback(() => {
    if (!editorRef.current) {
      return;
    }
    const html = editorRef.current.innerHTML;
    const nextValue = stripHtml(html) ? html : '';
    onChange(nextValue);
  }, [onChange]);

  const applyFormat = React.useCallback(
    (command) => {
      if (typeof document === 'undefined' || disabled) {
        return;
      }
      if (editorRef.current) {
        editorRef.current.focus();
      }
      document.execCommand(command, false, null);
      emitChange();
    },
    [disabled, emitChange]
  );

  const handlePaste = React.useCallback(
    (event) => {
      if (!editorRef.current) {
        return;
      }
      event.preventDefault();
      const text = event.clipboardData.getData('text/plain');
      document.execCommand('insertText', false, text);
      emitChange();
    },
    [emitChange]
  );

  const showPlaceholder = !isFocused && stripHtml(value || '').length === 0;

  return (
    <FormControl fullWidth error={error} disabled={disabled}>
      {label ? (
        <FormLabel component="legend" style={{ marginBottom: 4 }}>
          {label}
        </FormLabel>
      ) : null}
      <Paper variant="outlined">
        <Box display="flex" borderBottom={1} borderColor="divider" p={0.5}>
          {toolbarButtons.map(({ icon: Icon, command, label: buttonLabel }) => (
            <IconButton
              key={command}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => applyFormat(command)}
              size="small"
              aria-label={buttonLabel}
              disabled={disabled}
            >
              <Icon fontSize="small" />
            </IconButton>
          ))}
        </Box>
        <Box position="relative" minHeight={120}>
          {showPlaceholder ? (
            <Box
              position="absolute"
              top={8}
              left={12}
              color="text.secondary"
              pointerEvents="none"
              fontSize={14}
            >
              {placeholder}
            </Box>
          ) : null}
          <Box
            ref={editorRef}
            contentEditable={!disabled}
            suppressContentEditableWarning
            p={1.5}
            minHeight={120}
            style={{ outline: 'none' }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setIsFocused(false);
              emitChange();
            }}
            onInput={emitChange}
            onPaste={handlePaste}
          />
        </Box>
      </Paper>
      <FormHelperText>{helperText || ' '}</FormHelperText>
    </FormControl>
  );
});

RichTextEditor.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  helperText: PropTypes.string,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
};

RichTextEditor.defaultProps = {
  label: '',
  value: '',
  placeholder: '',
  helperText: '',
  error: false,
  disabled: false,
};

export default RichTextEditor;
export const stripHtmlContent = stripHtml;
export const toRichTextHtml = toEditorHtml;


